import { z } from "zod";

/** Inpatient Phase 3 — eMAR: medication orders + administration records. */

export const medRouteSchema = z.enum([
	"PO",
	"IV",
	"IM",
	"SC",
	"PR",
	"INH",
	"TOP",
	"other",
]);
export type MedRoute = z.infer<typeof medRouteSchema>;

export const medFrequencySchema = z.enum([
	"OD",
	"BD",
	"TDS",
	"QID",
	"q6h",
	"q8h",
	"PRN",
	"STAT",
	"other",
]);
export type MedFrequency = z.infer<typeof medFrequencySchema>;

export const administrationStatusSchema = z.enum([
	"given",
	"held",
	"refused",
	"missed",
]);
export type AdministrationStatus = z.infer<typeof administrationStatusSchema>;

// ===== Medication order =====
export const medicationOrderApiSchema = z.object({
	id: z.string().uuid(),
	admission_id: z.string().uuid(),
	drug_name: z.string(),
	dose: z.string().nullable(),
	route: z.string().nullable(),
	frequency: z.string().nullable(),
	instructions: z.string().nullable(),
	status: z.string(),
	ordered_by_member_id: z.string().uuid().nullable(),
	started_at: z.string().datetime(),
	discontinued_at: z.string().datetime().nullable(),
	created_at: z.string().datetime(),
	/** Recent administrations for this order (newest first), surfaced in the MAR. */
	administrations: z
		.array(z.lazy(() => medicationAdministrationApiSchema))
		.optional(),
});
export type MedicationOrderApi = z.infer<typeof medicationOrderApiSchema>;

export const createMedicationOrderInputSchema = z.object({
	admission_id: z.string().uuid(),
	drug_name: z.string().min(1).max(200),
	dose: z.string().max(100).nullable().optional(),
	route: medRouteSchema.nullable().optional(),
	frequency: medFrequencySchema.nullable().optional(),
	instructions: z.string().max(1000).nullable().optional(),
});
export type CreateMedicationOrderInput = z.infer<
	typeof createMedicationOrderInputSchema
>;

// ===== Administration =====
export const medicationAdministrationApiSchema = z.object({
	id: z.string().uuid(),
	order_id: z.string().uuid(),
	admission_id: z.string().uuid(),
	status: z.string(),
	administered_at: z.string().datetime(),
	administered_by_member_id: z.string().uuid().nullable(),
	note: z.string().nullable(),
});
export type MedicationAdministrationApi = z.infer<
	typeof medicationAdministrationApiSchema
>;

export const recordAdministrationInputSchema = z.object({
	order_id: z.string().uuid(),
	status: administrationStatusSchema,
	note: z.string().max(500).nullable().optional(),
});
export type RecordAdministrationInput = z.infer<
	typeof recordAdministrationInputSchema
>;
